/**
 * Home visit massager list hooks (訪問予定表 施術師毎 — /visits/massager_init).
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useClinicContext } from './useClinicContext';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import {
  searchHomeVisitMassagers,
  getHomeVisitMassagerMassagers,
} from '@/services/main/homeVisitMassager';
import type {
  HomeVisitMassagerSearchParams,
  HomeVisitMassagerSearchResponse,
  HomeVisitMassagerMassagersResponse,
  HomeVisitMassagerMassager,
} from '@/pages/main/homeVisitMassager/types';

function getServiceTypeFallback(): string {
  return typeof localStorage !== 'undefined'
    ? localStorage.getItem('service_type') || SERVICE_TYPE_A2
    : SERVICE_TYPE_A2;
}

function useServiceType(): string {
  const theme = useTheme();
  return theme?.serviceType === SERVICE_TYPE_R2 || theme?.serviceType === SERVICE_TYPE_A2
    ? theme.serviceType
    : getServiceTypeFallback();
}

/**
 * Search visits for massager-wise schedule. Pass null to skip.
 */
export function useHomeVisitMassagerSearch(params: HomeVisitMassagerSearchParams | null) {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useServiceType();
  const toast = useAppToast();

  const { data, isLoading, isFetching, isError, isPending, refetch, error } = useQuery<
    HomeVisitMassagerSearchResponse,
    Error
  >({
    queryKey: [
      'home-visit-massagers',
      'search',
      tenant,
      clinicNumber,
      serviceType,
      params,
    ],
    queryFn: () => searchHomeVisitMassagers(tenant, clinicNumber, params!, serviceType),
    enabled: !!tenant && !!clinicNumber && params !== null,
    placeholderData: prev => prev,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  const responseData = data?.data;
  const rows = responseData?.data ?? [];
  const pagination = responseData?.pagination ?? null;

  return { rows, pagination, isLoading: isLoading || isFetching, isError, isPending, refetch, error };
}

/**
 * Massager dropdown — visible + available NOW (BD §3.4).
 */
export function useHomeVisitMassagerMassagers() {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useServiceType();
  const toast = useAppToast();

  const { data, isLoading, isError, isPending, refetch, error } = useQuery<
    HomeVisitMassagerMassagersResponse,
    Error
  >({
    queryKey: ['home-visit-massagers', 'massagers', tenant, clinicNumber, serviceType],
    queryFn: () => getHomeVisitMassagerMassagers(tenant, clinicNumber, serviceType),
    enabled: !!tenant && !!clinicNumber,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  const massagers: HomeVisitMassagerMassager[] = data?.data?.data ?? [];

  return { massagers, isLoading, isError, isPending, refetch, error };
}
